import React from 'react';

const sections = [
  {
    title: "Qu'est-ce que le SMIC ?",
    content: `Le SMIC, ou Salaire Minimum Interprofessionnel de Croissance, est le salaire horaire minimum légal en dessous duquel aucun salarié ne peut être rémunéré en France. Il s'applique à tous les salariés de plus de 18 ans, quel que soit le secteur d'activité ou la taille de l'entreprise. Le SMIC est fixé par décret gouvernemental et révisé au minimum chaque année au 1er janvier, en fonction de l'évolution de l'indice des prix à la consommation et du gain de pouvoir d'achat du salaire ouvrier.`,
  },
  {
    title: "Revalorisation au 1er janvier 2026",
    content: `Au 1er janvier 2026, le SMIC a été revalorisé par le Décret n° 2025-1451 du 29 décembre 2025. Le taux horaire brut est fixé à 12,02 €, soit 9,52 € net de l'heure. Le salaire mensuel brut s'élève à 1 823,03 € pour un temps plein (151,67 heures), soit 1 443,11 € net par mois après déduction des cotisations sociales salariales. Cette revalorisation garantit que le pouvoir d'achat des salariés au bas de l'échelle salariale suit l'inflation.`,
  },
  {
    title: "Quelle est la différence entre salaire brut et salaire net ?",
    content: `Le salaire brut est la rémunération totale négociée dans le contrat de travail, avant toute déduction. Le salaire net est ce que le salarié perçoit réellement sur son compte bancaire, après déduction des cotisations sociales salariales (assurance maladie, retraite, chômage, etc.). En France, le passage du brut au net représente en moyenne une réduction de 22 % pour un non-cadre et de 25 % pour un cadre. À ces prélèvements s'ajoute le prélèvement à la source de l'impôt sur le revenu, qui réduit encore le net perçu selon le taux d'imposition personnel du salarié.`,
  },
  {
    title: "Comment utiliser ce calculateur ?",
    content: `Notre outil vous permet de convertir instantanément un salaire brut en net, ou inversement, selon votre statut (non-cadre, cadre, fonctionnaire ou portage salarial). Ajustez votre taux d'imposition pour obtenir votre net après impôts, simulez un temps partiel, et visualisez la répartition de votre salaire. Le bouton « SMIC 2026 » pré-remplit automatiquement le calculateur avec le montant officiel du SMIC en vigueur au 1er janvier 2026 : 1 823,03 € brut mensuel (12,02 €/h brut — 9,52 €/h net).`,
  },
];

const cotisationsArticle = [
  {
    heading: "La retraite — la part la plus visible",
    badge: "~11 % du brut",
    badgeColor: "bg-indigo-50 text-indigo-600",
    body: `La cotisation retraite se décompose en deux régimes distincts. Le régime de base (CNAV) représente 6,90 % du salaire brut dans la limite du plafond de la Sécurité sociale. S'y ajoute la retraite complémentaire obligatoire (Agirc-Arrco), dont les taux varient selon la tranche de rémunération. Depuis la réforme de 2023, l'âge légal de départ est progressivement porté à 64 ans. Les cotisations alimentent un système par répartition : les actifs d'aujourd'hui financent directement les pensions des retraités actuels.`,
  },
  {
    heading: "La santé — un financement reconfiguré",
    badge: "via CSG",
    badgeColor: "bg-emerald-50 text-emerald-600",
    body: `Contrairement à une idée reçue, la cotisation salariale maladie a été supprimée en 2018 et remplacée par une hausse de la CSG. C'est désormais principalement via cet impôt que les salariés contribuent à l'Assurance maladie. Une partie des cotisations patronales (environ 13 % du brut) continue toutefois de financer la branche santé. Ces fonds permettent le remboursement des consultations, des médicaments, des hospitalisations, mais aussi le versement des indemnités journalières en cas d'arrêt maladie.`,
  },
  {
    heading: "CSG et CRDS — les prélèvements sur revenus",
    badge: "~9,70 % du brut",
    badgeColor: "bg-amber-50 text-amber-600",
    body: `La contribution sociale généralisée (CSG) est prélevée à 9,20 % sur 98,25 % du salaire brut. Elle est en partie déductible du revenu imposable (6,80 %). La CRDS (0,50 %) reste quant à elle non déductible et a été créée pour rembourser la dette de la Sécurité sociale. Au total, CSG et CRDS représentent environ 9,70 % du revenu brut.`,
  },
  {
    heading: "Ce que ça change concrètement",
    badge: "~500 €/mois",
    badgeColor: "bg-rose-50 text-rose-600",
    body: `Pour un salarié au salaire médian en France (environ 2 400 € brut en 2026), les cotisations salariales représentent un prélèvement d'environ 500 € par mois. En contrepartie, ce même salarié bénéficie d'une couverture maladie, de droits à la retraite, et d'une protection en cas d'invalidité ou de décès. À noter : depuis 2018, la cotisation chômage salariale a été supprimée pour les contrats non précaires. L'assurance chômage est désormais financée uniquement par les employeurs et par une partie de la CSG.`,
  },
];

const cadreArticle = [
  {
    heading: "La cotisation APEC — petite, mais symbolique",
    badge: "~0,024 %",
    badgeColor: "bg-indigo-50 text-indigo-600",
    body: `L'Association Pour l'Emploi des Cadres (APEC) est financée par une cotisation obligatoire dès l'accès au statut cadre. Son montant est modeste — environ 0,024 % du salaire brut plafonné à 4 fois le plafond de la Sécurité sociale — mais elle est emblématique du changement de régime. Elle finance les services d'accompagnement à l'emploi réservés aux cadres : bilans de compétences, conseils en mobilité, accès à des offres spécialisées. En pratique, son impact sur le net est négligeable.`,
  },
  {
    heading: "La prévoyance cadre — le vrai poste qui fait baisser le net",
    badge: "0,5 % – 2 %",
    badgeColor: "bg-rose-50 text-rose-600",
    body: `La CCN des cadres de 1947 impose à tout employeur de couvrir ses cadres par un contrat de prévoyance obligatoire (décès, invalidité, incapacité). La part salariale peut représenter entre 0,5 % et 2 % du salaire brut selon les accords de branche. Pour un cadre à 4 000 € brut, cela représente entre 20 € et 80 € de cotisation supplémentaire par mois par rapport à un non-cadre.`,
  },
  {
    heading: "Retraite complémentaire Agirc-Arrco",
    badge: "Tranche 2",
    badgeColor: "bg-amber-50 text-amber-600",
    body: `Depuis la fusion des régimes en 2019, le régime Agirc-Arrco est unifié. Mais au-delà du plafond de la Sécurité sociale, le taux de cotisation en tranche 2 s'applique sur une base plus large pour les cadres dont la rémunération dépasse ce plafond. Plus le salaire est élevé, plus la cotisation retraite complémentaire est importante en valeur absolue.`,
  },
  {
    heading: "Ce que le statut cadre apporte en contrepartie",
    badge: "Garanties +",
    badgeColor: "bg-emerald-50 text-emerald-600",
    body: `Les cotisations supplémentaires achètent des garanties réelles : maintien de salaire plus généreux en cas d'arrêt maladie prolongé, capital décès majoré, rente éducation pour les enfants. Le statut cadre ouvre aussi souvent droit à des jours de RTT supplémentaires, une clause de forfait-jours, et une reconnaissance conventionnelle facilitant les négociations salariales futures.`,
  },
];

export default function SeoContent() {
  return (
    <section className="max-w-4xl mx-auto mt-10 mb-4">

      {/* Existing SEO cards block */}
      <div className="border-t border-slate-200 pt-8">
        <h2 className="text-xl font-extrabold text-slate-800 mb-6">
          Tout savoir sur le SMIC et la conversion brut / net en France
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {sections.map((s, i) => (
            <div key={i} className="bg-white rounded-2xl p-5 ring-1 ring-slate-900/5 shadow-sm">
              <h3 className="text-sm font-bold text-slate-800 mb-2">{s.title}</h3>
              <p className="text-slate-500 text-sm leading-relaxed">{s.content}</p>
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-400 mt-5 text-center">
          Les montants indiqués sont basés sur le Décret n° 2025-1228 du 17 décembre 2025 fixant le SMIC au 1er janvier 2026. Ils sont donnés à titre indicatif et ne constituent pas un avis juridique ou fiscal.
        </p>
      </div>

      {/* Article: Comprendre les cotisations sociales en 2026 */}
      <article className="mt-12 border-t border-slate-200 pt-8">
        <header className="mb-6">
          <span className="inline-block text-xs font-semibold uppercase tracking-widest text-indigo-500 bg-indigo-50 px-3 py-1 rounded-full mb-3">
            Décryptage
          </span>
          <h2 className="text-xl font-extrabold text-slate-800 mb-2">
            Comprendre les cotisations sociales en 2026
          </h2>
          <p className="text-slate-500 text-sm leading-relaxed">
            Sur chaque fiche de paie, une part importante de la rémunération brute disparaît avant même d'arriver sur le compte. En France, les cotisations sociales constituent le pilier du système de protection sociale — elles financent la retraite, la santé, le chômage, les allocations familiales et d'autres prestations. Pour un salarié du secteur privé, ces prélèvements représentent environ <strong className="text-slate-700">22 % du salaire brut</strong> côté employé, auxquels s'ajoutent les cotisations patronales.
          </p>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {cotisationsArticle.map((item, i) => (
            <div key={i} className="bg-white rounded-2xl p-5 ring-1 ring-slate-900/5 shadow-sm flex flex-col gap-3">
              <div className="flex items-start justify-between gap-3">
                <h3 className="text-sm font-bold text-slate-800 leading-snug">{item.heading}</h3>
                <span className={`shrink-0 text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${item.badgeColor}`}>
                  {item.badge}
                </span>
              </div>
              <p className="text-slate-500 text-sm leading-relaxed">{item.body}</p>
            </div>
          ))}
        </div>

        <div className="mt-6 bg-slate-50 border border-slate-200 rounded-2xl p-5">
          <p className="text-sm text-slate-600 leading-relaxed">
            <span className="font-semibold text-slate-800">À retenir :</span> Le système français est régulièrement réformé. Suivre les évolutions des taux sur le site de l'
            <a
              href="https://www.urssaf.fr"
              target="_blank"
              rel="noopener noreferrer"
              className="text-indigo-600 hover:underline font-medium"
            >
              Urssaf
            </a>
            {' '}ou via votre service RH reste le moyen le plus fiable de comprendre exactement ce qui figure sur votre fiche de paie.
          </p>
        </div>
      </article>

      {/* Article: Non-Cadre à Cadre */}
      <article className="mt-12 border-t border-slate-200 pt-8">
        <header className="mb-6">
          <span className="inline-block text-xs font-semibold uppercase tracking-widest text-violet-500 bg-violet-50 px-3 py-1 rounded-full mb-3">
            Analyse
          </span>
          <h2 className="text-xl font-extrabold text-slate-800 mb-2">
            Du statut Non-Cadre au statut Cadre : pourquoi votre salaire net peut baisser
          </h2>
          <p className="text-slate-500 text-sm leading-relaxed">
            On vous annonce une promotion, un nouveau titre, un statut cadre. Bonne nouvelle, en apparence. Puis la première fiche de paie arrive — et le net est inférieur à ce que vous espériez, parfois même à ce que vous touchiez avant. Ce paradoxe s'explique par une mécanique simple, mais rarement expliquée. Le statut cadre entraîne un basculement vers des régimes de protection sociale spécifiques, obligatoires, et <strong className="text-slate-700">plus coûteux pour le salarié</strong>.
          </p>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {cadreArticle.map((item, i) => (
            <div key={i} className="bg-white rounded-2xl p-5 ring-1 ring-slate-900/5 shadow-sm flex flex-col gap-3">
              <div className="flex items-start justify-between gap-3">
                <h3 className="text-sm font-bold text-slate-800 leading-snug">{item.heading}</h3>
                <span className={`shrink-0 text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${item.badgeColor}`}>
                  {item.badge}
                </span>
              </div>
              <p className="text-slate-500 text-sm leading-relaxed">{item.body}</p>
            </div>
          ))}
        </div>

        {/* Concrete example */}
        <div className="mt-6 bg-slate-50 border border-slate-200 rounded-2xl p-5 flex flex-col gap-4">
          <h3 className="text-sm font-bold text-slate-800">Un exemple concret</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl p-4 ring-1 ring-slate-900/5">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Non-cadre</p>
              <p className="text-2xl font-extrabold text-slate-800">3 200 € <span className="text-sm font-medium text-slate-400">brut</span></p>
              <p className="text-sm text-slate-500 mt-1">Cotisations ~22 % → <strong className="text-slate-700">≈ 704 €</strong></p>
              <p className="text-sm text-emerald-600 font-semibold mt-1">Net ≈ 2 496 €</p>
            </div>
            <div className="bg-white rounded-xl p-4 ring-1 ring-slate-900/5">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Cadre (après promotion)</p>
              <p className="text-2xl font-extrabold text-slate-800">3 400 € <span className="text-sm font-medium text-slate-400">brut</span></p>
              <p className="text-sm text-slate-500 mt-1">Cotisations ~24–25 % → <strong className="text-slate-700">≈ 816–850 €</strong></p>
              <p className="text-sm text-emerald-600 font-semibold mt-1">Net ≈ 2 550 – 2 584 €</p>
            </div>
          </div>
          <p className="text-sm text-slate-500 leading-relaxed">
            Le gain net réel n'est donc que de <strong className="text-slate-700">50 à 90 € par mois</strong> pour une augmentation brute de 200 €. Si la promotion n'est pas accompagnée d'une revalorisation suffisante, le net peut stagner, voire reculer légèrement.
          </p>
        </div>

        {/* Key takeaway */}
        <div className="mt-4 bg-violet-50 border border-violet-100 rounded-2xl p-5">
          <p className="text-sm text-slate-600 leading-relaxed">
            <span className="font-semibold text-slate-800">Ce qu'il faut retenir :</span> Le salaire brut doit augmenter d'au moins <strong className="text-slate-700">3 à 4 %</strong> lors du passage au statut cadre pour que le net reste stable. La bonne question à poser lors d'une négociation n'est donc pas <em>« quel est mon nouveau brut ? »</em> mais <em>« quel sera mon net, et quelles garanties de prévoyance sont incluses dans le contrat ? »</em>
          </p>
        </div>
      </article>

    </section>
  );
}